import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { api, formatApiError } from "../../../lib/api";
import { COMMON_PLACEHOLDERS, SKELETON } from "./feeUtils";

function TemplateForm({ tpl, onDone, onCancel, categories = [] }) {
    const [name, setName] = useState(tpl?.name || "");
    const [purpose, setPurpose] = useState(tpl?.purpose || "");
    const [category, setCategory] = useState(tpl?.category || "special");
    const [mode, setMode] = useState(tpl?.mode || "both");
    const [body, setBody] = useState(tpl?.body || SKELETON);
    const [saving, setSaving] = useState(false);
    const insert = (p) => setBody((b) => `${b}[${p}]`);
    const save = async () => {
        if (!name.trim() || !body.trim()) return toast.error("Template name and message are required");
        setSaving(true);
        try {
            const res = tpl?.id
                ? await api.put(`/admin/fee-reminders/templates/${tpl.id}`, { name, purpose, body, category, mode })
                : await api.post("/admin/fee-reminders/templates", { name, purpose, body, category, mode });
            toast.success("Template saved");
            onDone(res.data.template);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
    };
    return (
        <div className="space-y-3" data-testid="fee-template-form">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name" data-testid="fee-template-name"
                className="w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm font-semibold" />
            <div className="grid sm:grid-cols-2 gap-2">
                <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="fee-template-category" className="h-11 px-2 rounded-lg border border-slate-300 text-sm bg-white">
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="fee-template-mode" className="h-11 px-2 rounded-lg border border-slate-300 text-sm bg-white">
                    <option value="both">Home Tuition &amp; Online</option><option value="home">Home Tuition only</option><option value="online">1:1 Live Online only</option>
                </select>
            </div>
            <input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Use when… (e.g. The agreed fee payment date is today)" data-testid="fee-template-purpose-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} data-testid="fee-template-body"
                className="w-full p-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm font-mono leading-relaxed" />
            <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-xs text-slate-500">Add placeholder:</span>
                {COMMON_PLACEHOLDERS.map((p) => (
                    <button key={p} type="button" onClick={() => insert(p)} data-testid={`fee-insert-${p.toLowerCase().replace(/\s/g, "-")}`}
                        className="text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 font-mono">[{p}]</button>
                ))}
            </div>
            <div className="flex gap-2 justify-end">
                <button type="button" onClick={onCancel} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-semibold" data-testid="fee-template-cancel">Cancel</button>
                <button type="button" onClick={save} disabled={saving} data-testid="fee-template-save"
                    className="h-10 px-4 rounded-lg bg-[#1E3A8A] text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save template
                </button>
            </div>
        </div>
    );
}

export default function TemplateManager({ open, onOpenChange, templates, categories = [], onChanged }) {
    const [editing, setEditing] = useState(null); // template | {} for new
    const [q, setQ] = useState("");
    const catLabel = (k) => categories.find((c) => c.key === k)?.label || "";
    const catOrder = (k) => { const i = categories.findIndex((c) => c.key === k); return i < 0 ? 99 : i; };
    const shown = templates
        .filter((t) => !q.trim() || `${t.name} ${t.purpose} ${catLabel(t.category)}`.toLowerCase().includes(q.trim().toLowerCase()))
        .slice().sort((a, b) => catOrder(a.category) - catOrder(b.category) || (a.order ?? 0) - (b.order ?? 0));
    const remove = async (t) => {
        if (!window.confirm(`Delete the template "${t.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/fee-reminders/templates/${t.id}`);
            toast.success("Template deleted");
            onChanged();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    const restore = async () => {
        try {
            const res = await api.post("/admin/fee-reminders/templates/restore-defaults");
            toast.success(res.data.added ? `Restored ${res.data.added} default template(s)` : "All default templates are already present");
            onChanged();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => { setEditing(null); onOpenChange(o); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto grid-cols-1 z-[70]" data-testid="fee-template-manager">
                <DialogHeader>
                    <DialogTitle>Manage Templates</DialogTitle>
                    <DialogDescription>Create, edit, rename or delete reminder templates. Changes are saved for everyone on the team.</DialogDescription>
                </DialogHeader>
                {editing ? (
                    <TemplateForm categories={categories} tpl={editing.id ? editing : null} onCancel={() => setEditing(null)}
                        onDone={() => { setEditing(null); onChanged(); }} />
                ) : (
                    <div className="space-y-2">
                        <button type="button" onClick={() => setEditing({})} data-testid="fee-template-new"
                            className="w-full h-11 rounded-lg border-2 border-dashed border-slate-300 text-sm font-bold text-[#1E3A8A] hover:border-[#1E3A8A] inline-flex items-center justify-center gap-2">
                            <Plus className="w-4 h-4" /> New template
                        </button>
                        <button type="button" onClick={restore} data-testid="fee-template-restore"
                            className="text-xs font-semibold text-slate-500 hover:text-[#1E3A8A] hover:underline">Restore missing default templates</button>
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${templates.length} templates`} data-testid="fee-template-search"
                            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm" />
                        {shown.map((t, i) => (
                            <div key={t.id} className="flex items-center gap-2 border border-slate-200 rounded-lg p-3" data-testid={`fee-template-row-${i}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-[#0F172A] truncate">{t.name}</div>
                                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 truncate">{catLabel(t.category)}{t.mode !== "both" ? ` · ${t.mode === "home" ? "Home Tuition" : "1:1 Online"}` : ""}</div>
                                    <div className="text-xs text-slate-500 truncate">{t.purpose ? `Use when: ${t.purpose}` : t.body.replace(/\s+/g, " ").slice(0, 110)}</div>
                                </div>
                                <button type="button" onClick={() => setEditing(t)} aria-label="Edit template" data-testid={`fee-template-edit-${i}`}
                                    className="w-9 h-9 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-600"><Pencil className="w-4 h-4" /></button>
                                <button type="button" onClick={() => remove(t)} aria-label="Delete template" data-testid={`fee-template-delete-${i}`}
                                    className="w-9 h-9 grid place-items-center rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
